// 首页
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Empty, Layout, Skeleton, Tabs, message } from 'antd';
import MainFooter from '@/components/mainfooter';
import MainHeader from '@/components/mainheader';
import AccountModerationNotice from '@/components/accountmoderationnotice';
import PullToRefresh from '@/components/pulltorefresh';
import PostCard from '@/components/postcard';
import Discovery from '@/components/discovery';
import PostFilters from '@/components/postfilters';
import PostFeedActions from '@/components/postfeedactions';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import SchoolNotice from '@/components/schoolnotice';
import { BankOutlined } from '@ant-design/icons';
import { loadPosts } from '@/store/modules/posts';
import { useBrowseState } from '@/utils/browseState';
import './App.scss';

const { Content } = Layout;

const App = () => {
  const [mode, setMode] = useBrowseState('home:mode', 'latest');
  const [baseFilters, setFilters, restored] = useBrowseState('home:filters', {});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 480);
  const { isAuthenticated, userInfo } = useSelector(state => state.user);
  const school = isAuthenticated ? userInfo?.school : null;
  const canBrowse = !isAuthenticated || Boolean(school);
  const filters = useMemo(() => {
    const current = { ...baseFilters }; delete current.school;
    return school ? { ...current, school } : current;
  }, [baseFilters, school]);
  const { list: storedList, listPagination, listStatus, listError, listErrorStatus, listRequestedPage, listFilters } = useSelector(state => state.posts);
  const inScope = String(listFilters?.school ?? '') === String(school ?? '');
  const list = inScope ? storedList : [];
  const resumeQuery = useRef(restored && listStatus === 'succeeded' && JSON.stringify(filters) === JSON.stringify(listFilters) ? JSON.stringify(filters) : null);
  const hasMore = inScope && listPagination && listPagination.page < listPagination.totalPages;

  useEffect(() => {
    if (!canBrowse) return;
    if (resumeQuery.current === JSON.stringify(filters)) return;
    resumeQuery.current = null;
    dispatch(loadPosts({ page: 1, filters }));
  }, [dispatch, filters, canBrowse]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleRefresh = useCallback(() => dispatch(loadPosts({ page: 1, filters })).unwrap(), [dispatch, filters]);

  const handlePost = () => {
    if (isAuthenticated) {
      navigate('/posts/create');
    } else {
      message.warning('请先登录后发帖');
      navigate('/login');
    }
  };

  const navigation = isAuthenticated && <Tabs activeKey={mode} onChange={setMode} items={[{ key: 'latest', label: '最新' }, { key: 'recommended', label: '为你推荐' }, { key: 'nearby', label: '附近招募' }]} />;

  return (
    <div className="app">
      <Layout>
        <MainHeader />
        <Content className="post-feed-content">
          <AccountModerationNotice />
          {isAuthenticated && canBrowse && <div className="home-school-bar">
            <div className="home-school-current"><span className="home-school-bar-label"><BankOutlined aria-hidden="true" />当前学校</span><strong>{userInfo?.school_name || '已选择学校'}</strong></div>
            <Link className="home-school-bar-action" to="/personal/info">学校设置</Link>
          </div>}
          {!canBrowse ? <SchoolNotice /> : isAuthenticated && mode !== 'latest' ? <Discovery key={`${mode}:${school}`} mode={mode} isMobile={isMobile} navigation={navigation} CardComponent={PostCard} onPost={handlePost} /> : <>
          <PostFilters onChange={setFilters} disabled={listStatus === 'loading'} isMobile={isMobile} navigation={navigation} />
          <PullToRefresh enabled={isMobile} disabled={listStatus === 'loading'} onRefresh={handleRefresh}>
          <section className="post-feed" aria-label="帖子列表">
            {listStatus === 'loading' && list.length === 0 && (
              <div className="post-feed-list" aria-label="正在加载帖子">
                {[1, 2, 3].map(item => (
                  <div className="post-feed-card" key={item}><Skeleton active avatar paragraph={{ rows: 3 }} /></div>
                ))}
              </div>
            )}

            {list.length > 0 && (
              <div className="post-feed-list">
                {list.map(post => <PostCard key={post.id} post={post} />)}
              </div>
            )}

            {listStatus === 'failed' && (
              <Alert
                type="error"
                showIcon
                message={listError || '帖子加载失败，请稍后重试'}
                action={listErrorStatus === 409 && /学校/.test(listError || '') ? <Button size="small" onClick={() => navigate(0)}>重新加载页面</Button> : <Button size="small" onClick={() => dispatch(loadPosts({ page: listRequestedPage, filters }))}>重试</Button>}
              />
            )}

            {listStatus === 'succeeded' && list.length === 0 && (
              <Empty description="目前还没有帖子，来发布第一条搭子招募吧" />
            )}

            {hasMore && listStatus !== 'failed' && (
              <Button className="post-feed-more" loading={listStatus === 'loading'} onClick={() => dispatch(loadPosts({ page: listPagination.page + 1, filters }))}>
                加载更多
              </Button>
            )}
          </section>
          </PullToRefresh></>}
        </Content>
        <MainFooter />
      </Layout>
      {canBrowse && (!isAuthenticated || mode === 'latest') && <PostFeedActions isMobile={isMobile} onPost={handlePost}
        loading={listStatus === 'loading'} disabled={listStatus === 'loading'} onRefresh={() => dispatch(loadPosts({ page: 1, filters }))} />}
    </div>
  );
};

export default App;
